// PWA Utilities - Helper functions for PWA features

/**
 * Check if the app is running in PWA/standalone mode
 */
export const isStandaloneMode = () => {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true ||
    document.referrer.includes('android-app://')
  );
};

/**
 * Check if the app is online
 */
export const isOnline = () => {
  return navigator.onLine;
};

/**
 * Check if the app is offline
 */
export const isOffline = () => {
  return !navigator.onLine;
};

/**
 * Request notification permission
 */
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.log('This browser does not support notifications');
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    try {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  return false;
};

/**
 * Send a notification
 */
export const sendNotification = (title, options = {}) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    return new Notification(title, {
      icon: '/logo192.png',
      badge: '/favicon.ico',
      ...options,
    });
  }
};

/**
 * Subscribe to push notifications
 */
export const subscribeToPushNotifications = async () => {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.log('Push notifications are not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: process.env.REACT_APP_VAPID_PUBLIC_KEY,
    });
    console.log('Subscribed to push notifications:', subscription);
    return subscription;
  } catch (error) {
    console.error('Error subscribing to push notifications:', error);
    return null;
  }
};

/**
 * Unsubscribe from push notifications
 */
export const unsubscribeFromPushNotifications = async () => {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
      console.log('Unsubscribed from push notifications');
    }
  } catch (error) {
    console.error('Error unsubscribing from push notifications:', error);
  }
};

/**
 * Trigger background sync
 */
export const triggerBackgroundSync = async (tag = 'sync-inventory') => {
  if (!('serviceWorker' in navigator) || !('SyncManager' in window)) {
    console.log('Background sync is not supported');
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    await registration.sync.register(tag);
    console.log(`Background sync registered with tag: ${tag}`);
    return true;
  } catch (error) {
    console.error('Error registering background sync:', error);
    return false;
  }
};

/**
 * Check if app can be installed (PWA installable)
 */
export const canInstallApp = () => {
  return 'BeforeInstallPromptEvent' in window;
};

/**
 * Get permission status
 */
export const getPermissionStatus = async (permission) => {
  if (!('permissions' in navigator)) {
    return null;
  }

  try {
    const result = await navigator.permissions.query({ name: permission });
    return result.state;
  } catch (error) {
    console.error(`Error checking ${permission} permission:`, error);
    return null;
  }
};

/**
 * Cache specific URLs
 */
export const cacheUrls = async (urls) => {
  if (!('caches' in window)) {
    console.log('Cache API is not supported');
    return false;
  }

  try {
    const cache = await caches.open('stokraze-v1');
    await cache.addAll(urls);
    console.log('URLs cached successfully');
    return true;
  } catch (error) {
    console.error('Error caching URLs:', error);
    return false;
  }
};

/**
 * Clear all caches
 */
export const clearAllCaches = async () => {
  if (!('caches' in window)) {
    return false;
  }

  try {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((name) => caches.delete(name)));
    console.log('All caches cleared');
    return true;
  } catch (error) {
    console.error('Error clearing caches:', error);
    return false;
  }
};

/**
 * Get cache size
 */
export const getCacheSize = async () => {
  if (!('estimate' in navigator.storage)) {
    return null;
  }

  try {
    const estimate = await navigator.storage.estimate();
    return {
      usage: estimate.usage,
      quota: estimate.quota,
      percentage: Math.round((estimate.usage / estimate.quota) * 100),
    };
  } catch (error) {
    console.error('Error getting cache size:', error);
    return null;
  }
};

/**
 * Listen to online/offline status changes
 */
export const addOnlineOfflineListener = (callback) => {
  window.addEventListener('online', () => callback(true));
  window.addEventListener('offline', () => callback(false));

  // Return unsubscribe function
  return () => {
    window.removeEventListener('online', () => callback(true));
    window.removeEventListener('offline', () => callback(false));
  };
};

/**
 * Share content using Web Share API
 */
export const shareContent = async (data) => {
  if (!('share' in navigator)) {
    console.log('Web Share API is not supported');
    return false;
  }

  try {
    await navigator.share({
      title: 'Stokraze',
      ...data,
    });
    return true;
  } catch (error) {
    if (error.name !== 'AbortError') {
      console.error('Error sharing content:', error);
    }
    return false;
  }
};

/**
 * Add to home screen prompt
 */
export let deferredPrompt = null;

export const setupInstallPrompt = () => {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
  });
};

export const showInstallPrompt = () => {
  if (deferredPrompt) {
    deferredPrompt.prompt();
    deferredPrompt = null;
  }
};
